import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { COPY } from "../../copy";
import type { CumulativeSalesPoint } from "../../monthDetailData";
import {
  formatOverviewCurrency,
  formatOverviewRoas,
} from "../../p8OverviewData";
import { fillCopy } from "../../renderCopy";
import { useCardPaddingClass, useIsCompact } from "../CardDensity";
import { forecastTooltip } from "../ForecastExplainer";
import {
  ChartHeadline,
  ChartLegend,
  ChartLineLegendSwatch,
  CHART_LEGEND_GAP_CLASS,
  CHART_Y_AXIS_WIDTH,
  formatChartAxisCurrency,
  type ChartLegendItem,
} from "../overview/chartShared";

const SALES_COLOR = "#2d2a70";
/**
 * Shared with the Overview charts' forecast series. A reader arriving from the
 * Overview should recognize the projection without re-reading the legend.
 */
const FORECAST_COLOR = "#AFC1FF";
const AREA_FILL = "#e9e8f6";
const SPEND_COLOR = "#F26532";

const CHART_COPY = COPY.cohort.v2.chart;

/** What landed, where it's headed, and what it cost. */
function legendItems(month: string): ChartLegendItem[] {
  return [
    {
      key: "salesToDate",
      label: "Total ad sales",
      color: SALES_COLOR,
      tooltip: fillCopy(CHART_COPY.salesToDate, { month }),
    },
    {
      key: "projectedFirstYearSales",
      label: "First-year sales (forecasted)",
      color: FORECAST_COLOR,
      // The one forecasted series here, so it carries the explainer link the
      // Overview's forecast columns and ROAS legend also offer.
      tooltip: forecastTooltip(fillCopy(CHART_COPY.salesForecast, { month })),
      interactiveTooltip: true,
    },
    {
      key: "adSpend",
      label: "Ad spend",
      color: SPEND_COLOR,
      tooltip: fillCopy(CHART_COPY.adSpend, { month }),
    },
  ];
}

/** Ad spend is the only line in the legend; the other two are filled. */
function renderLegendSwatch(item: ChartLegendItem) {
  if (item.key !== "adSpend") {
    return undefined;
  }
  return <ChartLineLegendSwatch color={SPEND_COLOR} />;
}

type MonthTickProps = {
  x?: number;
  y?: number;
  payload?: { value: string };
};

function MonthAxisTick({ x, y, payload }: MonthTickProps) {
  if (x == null || y == null || !payload) {
    return null;
  }
  const [monthName = "", year = ""] = payload.value.trim().split(/\s+/);
  return (
    <g transform={`translate(${x},${y + 8})`}>
      <text textAnchor="middle" fill="#757575" fontSize={11}>
        <tspan x={0} dy={0}>
          {monthName}
        </tspan>
        <tspan x={0} dy={13}>
          {year}
        </tspan>
      </text>
    </g>
  );
}

type PlotDotProps = {
  cx?: number;
  cy?: number;
  index?: number;
  value?: number | null;
};

/**
 * Rounded square rather than a circle, matching the ROAS chart's markers. Recharts
 * calls this for every point including the null ones, so a missing value has to
 * return an empty node rather than nothing at all.
 */
function squareDot(
  { cx, cy, index, value }: PlotDotProps,
  { fill, size }: { fill: string; size: number }
) {
  const key = `${fill}-${index}`;
  if (value == null || cx == null || cy == null) {
    return <g key={key} />;
  }
  return (
    <rect
      key={key}
      x={cx - size / 2}
      y={cy - size / 2}
      width={size}
      height={size}
      rx={1.5}
      fill={fill}
      stroke="#fff"
      strokeWidth={1.5}
    />
  );
}

type TooltipEntry = { payload?: CumulativeSalesPoint };

type ChartTooltipProps = {
  active?: boolean;
  label?: string;
  payload?: TooltipEntry[];
  isV3?: boolean;
};

function ChartTooltip({ active, label, payload, isV3 }: ChartTooltipProps) {
  const row = payload?.[0]?.payload;
  if (!active || !row) {
    return null;
  }

  const forecastedSales = row.projectedFirstYearSales;
  const isForecastPoint = forecastedSales != null;

  return (
    <div className="bg-fs-surface-primary rounded-md border border-[#dfe0e1] px-3 py-2 text-[14px] leading-[20px] text-[#333] shadow-[0_2px_8px_rgba(0,0,0,0.15)]">
      <div className="mb-1 font-medium">{label}</div>
      {row.salesToDate != null && !(isV3 && isForecastPoint) ? (
        <div className="flex items-center gap-4">
          <span>Total ad sales</span>
          <span className="ml-auto font-medium">
            {formatOverviewCurrency(row.salesToDate)}
          </span>
        </div>
      ) : null}
      {forecastedSales != null ? (
        <div className="flex items-center gap-4">
          <span>
            {isV3 ? "First-year sales" : "First-year sales (forecasted)"}
          </span>
          <span className="ml-auto font-medium">
            {formatOverviewCurrency(forecastedSales)}
          </span>
        </div>
      ) : null}
      <div className="flex items-center gap-4">
        <span>Ad spend</span>
        <span className="ml-auto font-medium">
          {formatOverviewCurrency(row.adSpend)}
        </span>
      </div>
      {/* After spend, so the return sits next to the dollars it was divided
          by. Only where there are sales to date, so a hover past the present
          doesn't report a return on a figure the chart hasn't plotted. */}
      {isV3 && isForecastPoint && row.projectedRoas != null ? (
        <div className="flex items-center gap-4">
          <span>First-year ROAS</span>
          <span className="ml-auto font-medium">
            {formatOverviewRoas(row.projectedRoas)}
          </span>
        </div>
      ) : row.salesToDate != null && row.roasToDate != null ? (
        <div className="flex items-center gap-4">
          <span>ROAS to date</span>
          <span className="ml-auto font-medium">
            {formatOverviewRoas(row.roasToDate)}
          </span>
        </div>
      ) : null}
    </div>
  );
}

export function FirstYearSalesAndSpendChart({
  data,
  isV3 = false,
}: {
  data: CumulativeSalesPoint[];
  isV3?: boolean;
}) {
  const cardPaddingClass = useCardPaddingClass();
  const isCompact = useIsCompact();
  // Label one month per quarter so the two-line ticks don't collide, stepping to
  // every six months for the long windows older cohorts produce.
  const tickInterval = data.length > 20 ? 5 : 2;
  // The cohort month in the chart's own short form — it's the first x-axis tick,
  // so taking it from the series keeps the title, the legend tooltips and the
  // axis spelling it the same way rather than mixing in "January 2026".
  const cohortMonth = data[0]?.month ?? "";

  return (
    <div
      className={`bg-fs-surface-primary flex w-full flex-col rounded-lg border border-[#dfe0e1] ${cardPaddingClass}`}
    >
      {/* `description` is what ChartHeadline hangs the title's tooltip off, the
          same way the Overview charts do. */}
      <ChartHeadline
        title={fillCopy(CHART_COPY.title, { month: cohortMonth })}
        description={fillCopy(CHART_COPY.titleTooltip, { month: cohortMonth })}
      />

      <div className={`mt-4 flex flex-col ${CHART_LEGEND_GAP_CLASS}`}>
        {/* V3 plots at the same height as the Overview pair, so the two
            surfaces read at one scale. */}
        <div className={isCompact ? "h-[240px]" : "h-[220px]"}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={data}
              margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
            >
              <CartesianGrid
                stroke="#DFE0E1"
                strokeDasharray="3 0"
                vertical={false}
              />
              <XAxis
                dataKey="month"
                type="category"
                scale="point"
                padding={{ left: 12, right: 12 }}
                tickLine={false}
                axisLine={{ stroke: "#DFE0E1" }}
                interval={tickInterval}
                height={44}
                tick={(props) => <MonthAxisTick {...props} />}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#757575" }}
                tickLine={false}
                axisLine={false}
                tickFormatter={formatChartAxisCurrency}
                width={CHART_Y_AXIS_WIDTH}
              />
              <Tooltip
                cursor={{ stroke: "#dfe0e1", strokeWidth: 1 }}
                content={(props) => (
                  <ChartTooltip {...(props as ChartTooltipProps)} isV3={isV3} />
                )}
              />
              <Area
                type="linear"
                dataKey="salesToDate"
                stroke={SALES_COLOR}
                strokeWidth={1.5}
                fill={AREA_FILL}
                connectNulls={false}
                isAnimationActive={false}
                dot={(props: PlotDotProps) =>
                  squareDot(props, { fill: SALES_COLOR, size: 8 })
                }
                activeDot={{
                  r: 4.5,
                  fill: SALES_COLOR,
                  stroke: "#fff",
                  strokeWidth: 1.5,
                }}
              />
              {/* Two-point dotted bridge from the present to the projection. */}
              <Line
                type="linear"
                dataKey="projectedConnector"
                stroke={FORECAST_COLOR}
                strokeWidth={1.5}
                strokeDasharray="2 3"
                connectNulls
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
              {/* Single marker at the +12-month mark. */}
              <Line
                type="linear"
                dataKey="projectedFirstYearSales"
                stroke="transparent"
                connectNulls={false}
                isAnimationActive={false}
                dot={(props: PlotDotProps) =>
                  squareDot(props, { fill: FORECAST_COLOR, size: 10 })
                }
                activeDot={false}
              />
              <Line
                type="linear"
                dataKey="adSpend"
                stroke={SPEND_COLOR}
                strokeWidth={1.5}
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <ChartLegend
          items={legendItems(cohortMonth)}
          renderSwatch={renderLegendSwatch}
        />
      </div>
    </div>
  );
}
